'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/lib/auth/context';
import { POPULAR_LOCATIONS, LocationPreset } from '@/lib/location/resolver';
import { LocationAutocomplete } from '@/components/quick-calculator/LocationAutocomplete';
import { createBirthProfile, createChart, calculateTransientChartApi } from '@/lib/api/client';
import { Calendar, Clock, User, Sparkles, AlertCircle, Loader2, Lock } from 'lucide-react';

interface ReportOrderFormProps {
  locale: string;
  isNameOptional?: boolean;
  labels: {
    formTitle: string;
    formSubtitle: string;
    nameLabel: string;
    namePlaceholder: string;
    dateLabel: string;
    timeLabel: string;
    placeLabel: string;
    placePlaceholder: string;
    seeFreeChart: string;
    orderFullDisabled: string;
    privacyNotice: string;
  };
}

export function ReportOrderForm({ locale, labels, isNameOptional = false }: ReportOrderFormProps) {
  const router = useRouter();
  const { user } = useAuth();

  const [name, setName] = useState('');
  const [birthDate, setBirthDate] = useState('1990-01-01');
  const [birthTime, setBirthTime] = useState('12:00:00');
  const [locationName, setLocationName] = useState('New Delhi, Delhi, India');
  const [confirmedPreset, setConfirmedPreset] = useState<LocationPreset | null>(POPULAR_LOCATIONS[0]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isHi = locale === 'hi';
  const localizeHref = (path: string) => {
    if (!isHi) return path;
    return path === '/' ? '/hi' : `/hi${path}`;
  };

  const handleSelectLocation = (preset: LocationPreset) => {
    setConfirmedPreset(preset);
    setLocationName(preset.name);
    setError(null);
  };

  const handleClearLocation = () => {
    setConfirmedPreset(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmedName = name.trim();
    if (!isNameOptional && !trimmedName) {
      setError(isHi ? 'कृपया अपना पूरा नाम दर्ज करें।' : 'Please enter your full name.');
      return;
    }
    if (!birthDate) {
      setError(isHi ? 'कृपया मान्य जन्म तिथि दर्ज करें।' : 'Please enter a valid birth date.');
      return;
    }
    if (!birthTime) {
      setError(isHi ? 'कृपया मान्य जन्म समय दर्ज करें।' : 'Please enter a valid birth time.');
      return;
    }
    if (!confirmedPreset) {
      setError(isHi ? 'कृपया सूची से एक जन्म स्थान चुनें।' : 'Please select a confirmed birth place from the list.');
      return;
    }

    try {
      setLoading(true);
      const normalizedTime = birthTime.length === 5 ? `${birthTime}:00` : birthTime;

      const fallbackName = isHi ? 'शिशु' : 'Baby';
      const effectiveName = trimmedName || (isNameOptional ? fallbackName : 'Sample Person');

      const profileInput = {
        name: effectiveName,
        birthDate,
        birthTime: normalizedTime,
        latitude: confirmedPreset.latitude,
        longitude: confirmedPreset.longitude,
        timezone: confirmedPreset.timezone,
        locationName: confirmedPreset.name,
      };

      if (user) {
        const profile = await createBirthProfile(profileInput);
        const chart = await createChart(profile.id, undefined, 'lahiri');
        router.push(localizeHref(`/chart/${chart.id}`));
      } else {
        try {
          await calculateTransientChartApi(profileInput);
        } catch {
          // If guest API not reachable, proceed to birth chart calculator page
        }
        router.push(localizeHref('/birth-chart-calculator'));
      }
    } catch (err: any) {
      setError(err.message || (isHi ? 'गणना के दौरान त्रुटि हुई।' : 'An error occurred during calculation.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="bg-[rgba(5,10,36,0.95)] border border-white/20 rounded-3xl p-6 sm:p-8 shadow-[0_20px_50px_rgba(0,0,0,0.5)] backdrop-blur-xl relative"
      data-testid="report-order-form-container"
    >
      <div className="mb-6">
        <h3 className="text-xl sm:text-2xl font-['Lexend'] font-semibold text-white tracking-tight">
          {labels.formTitle}
        </h3>
        <p className="text-xs sm:text-sm text-[#dcecff]/75 font-['Outfit'] mt-1 leading-relaxed">
          {labels.formSubtitle}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4" data-testid="report-order-form">
        {error && (
          <div
            className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-300 text-xs flex items-center gap-2"
            role="alert"
          >
            <AlertCircle className="w-4 h-4 shrink-0 text-red-400" />
            <span>{error}</span>
          </div>
        )}

        {/* Full Name */}
        <div>
          <label
            htmlFor="birth-name"
            className="block text-xs font-medium text-[#dcecff]/80 font-['Outfit'] mb-1.5"
          >
            {labels.nameLabel}
          </label>
          <div className="relative">
            <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#dcecff]/50 pointer-events-none" />
            <input
              id="birth-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={labels.namePlaceholder}
              className="w-full h-11 pl-10 pr-3 rounded-xl bg-white/[0.05] border border-white/18 text-white placeholder-[#dcecff]/40 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-[#5ab4ff] focus:border-transparent transition-all"
            />
          </div>
        </div>

        {/* Date and Time Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="birth-date"
              className="block text-xs font-medium text-[#dcecff]/80 font-['Outfit'] mb-1.5"
            >
              {labels.dateLabel}
            </label>
            <div className="relative">
              <Calendar className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#dcecff]/50 pointer-events-none" />
              <input
                id="birth-date"
                type="date"
                value={birthDate}
                onChange={(e) => setBirthDate(e.target.value)}
                className="w-full h-11 pl-10 pr-3 rounded-xl bg-white/[0.05] border border-white/18 text-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-[#5ab4ff] focus:border-transparent transition-all"
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="birth-time"
              className="block text-xs font-medium text-[#dcecff]/80 font-['Outfit'] mb-1.5"
            >
              {labels.timeLabel}
            </label>
            <div className="relative">
              <Clock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#dcecff]/50 pointer-events-none" />
              <input
                id="birth-time"
                type="time"
                step="60"
                value={birthTime}
                onChange={(e) => setBirthTime(e.target.value)}
                className="w-full h-11 pl-10 pr-3 rounded-xl bg-white/[0.05] border border-white/18 text-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-[#5ab4ff] focus:border-transparent transition-all"
              />
            </div>
          </div>
        </div>

        {/* Place of Birth with LocationAutocomplete */}
        <div>
          <label className="block text-xs font-medium text-[#dcecff]/80 font-['Outfit'] mb-1.5">
            {labels.placeLabel}
          </label>
          <LocationAutocomplete
            value={locationName}
            onSelectLocation={handleSelectLocation}
            onClearLocation={handleClearLocation}
            confirmedPreset={confirmedPreset}
            idPrefix="order-form-loc"
          />
        </div>

        {/* Primary Action: Free Chart Preview */}
        <div className="pt-2">
          <button
            type="submit"
            disabled={loading}
            className="w-full h-12 px-6 rounded-full bg-white text-[#03163d] font-semibold text-xs sm:text-sm hover:bg-white/90 shadow-[0_0_25px_rgba(150,165,255,0.4)] transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5ab4ff] active:scale-[0.99]"
            data-testid="see-free-chart-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin text-[#03163d]" />
                <span>{isHi ? 'गणना जारी है...' : 'Calculating chart...'}</span>
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 text-[#03163d]" />
                <span>{labels.seeFreeChart}</span>
              </>
            )}
          </button>
        </div>

        {/* Disabled Full Report Order Button */}
        <div>
          <button
            type="button"
            disabled
            aria-disabled="true"
            className="w-full h-11 px-6 rounded-full bg-white/[0.04] border border-white/12 text-[#dcecff]/40 font-medium text-xs sm:text-sm flex items-center justify-center gap-2 cursor-not-allowed select-none opacity-60"
            data-testid="order-report-disabled-btn"
          >
            <Lock className="w-3.5 h-3.5 text-[#ffd36b]/60" />
            <span>{labels.orderFullDisabled}</span>
          </button>
        </div>

        {/* Privacy reassurance note */}
        <p className="text-[11px] text-[#dcecff]/60 text-center font-['Outfit'] pt-1 leading-normal">
          {labels.privacyNotice}
        </p>
      </form>
    </div>
  );
}
